import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import { tone } from '@/components/agents/primitives';
import { imageSrcSet, imageUrl } from '@/components/interior-landing/assets';

import { guidePath } from './pages';
import type { StyleGuideCard } from './types';

/** Guides as picture links: the same living room in each style. */
export default function GuideCards({
  guides,
  className,
  sizes,
}: {
  guides: StyleGuideCard[];
  className?: string;
  /** `sizes` for the pictures' srcset. */
  sizes: string;
}) {
  return (
    <ul className={clsxm('grid gap-x-5 gap-y-9', className)}>
      {guides.map((guide) => (
        <li key={guide.slug} className='min-w-0'>
          <Link
            href={guidePath(guide.slug)}
            className='group block rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600'
          >
            <div
              className='relative overflow-hidden rounded-2xl bg-[#e9e9f2] ring-1 ring-[#0f0f28]/[0.07] dark:bg-[#1b1b28] dark:ring-white/10'
              style={{ aspectRatio: `${guide.image.aspect}` }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={imageUrl(guide.image, 640)}
                srcSet={imageSrcSet(guide.image)}
                sizes={sizes}
                alt={`A living room in ${guide.name} style`}
                loading='lazy'
                decoding='async'
                className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]'
              />
            </div>
            <h3
              className={clsxm(
                'mt-4 text-[17px] font-semibold leading-snug',
                tone.ink,
              )}
            >
              {guide.name}
            </h3>
            <p className={clsxm('mt-1 text-[15px] leading-6', tone.body)}>
              {guide.tagline}
            </p>
          </Link>
        </li>
      ))}
    </ul>
  );
}
